import { registerLocaleData } from "@angular/common";
import localePt from "@angular/common/locales/pt";
import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  computed,
  signal
} from "@angular/core";
import { NfceDownloadType, NotaFiscalConsumidor } from "@interfaces/nfce";
import { ModalService } from "@services/modal.service";
import { NfceService } from "@services/nfce.service";
import { MenuItem } from "primeng/api";
import { OverlayPanel } from "primeng/overlaypanel";
import { Subscription } from "rxjs";
import {
  openDownloadPopupIfNeeded,
  openInExternalBrowser,
  preOpenExternalDownloadTab,
  saveBlobFile,
  shouldUseExternalDownloadLink
} from "src/app/utils/file-download.util";
import {
  formatarTempoRestante,
  segundosParaCancelar
} from "src/app/utils/nfce.util";

// O app não define LOCALE_ID: valores em reais são exibidos em pt-BR
// (R$ 1.234,56) pelo próprio componente, como no relatório por cliente.
registerLocaleData(localePt, "pt-BR");

export type NfceCardLayout = "card" | "row";

/**
 * Card da NFC-e na listagem. Diferenças para o card de NF-e: não há carta de
 * correção nem reimpressão; há XML de cancelamento, reenvio de e-mail e o
 * prazo curto de cancelamento (30 min por padrão), mostrado em contagem
 * regressiva enquanto cancelar ainda é possível.
 */
@Component({
  selector: "app-nfce-card",
  templateUrl: "./nfce-card.component.html",
  styleUrls: ["./nfce-card.component.scss"]
})
export class NfceCardComponent implements OnInit {
  @Input() nfce?: NotaFiscalConsumidor;
  /** "card" na grade; "row" na visualização em lista da listagem de NFC-e. */
  @Input() layout: NfceCardLayout = "card";
  /** Emitido após cancelamento/sincronização para a lista recarregar. */
  @Output() changed = new EventEmitter<void>();
  @ViewChild("overlay") overlay!: OverlayPanel;

  $: Subscription[] = [];
  items = signal<MenuItem[]>([]);
  menuOptions = computed(() => [{ items: this.items() }]);

  // Contagem regressiva do prazo de cancelamento (atualiza a cada segundo).
  segundosRestantes = signal<number | null>(null);
  tempoRestante = computed(() =>
    formatarTempoRestante(this.segundosRestantes())
  );
  private timer?: ReturnType<typeof setInterval>;

  get status() {
    return (this.nfce?.status ?? "").toUpperCase();
  }

  get autorizada() {
    return this.status === "CONCLUIDO";
  }

  get cancelada() {
    return this.status === "CANCELADO";
  }

  get rejeitada() {
    return this.status === "REJEITADO";
  }

  get processando() {
    return this.status === "PROCESSANDO" || !this.status;
  }

  get canDownloadFiles() {
    return this.autorizada || this.cancelada;
  }

  get podeCancelar() {
    if (!this.autorizada) return false;
    const segundos = this.segundosRestantes();
    return segundos === null ? !!this.nfce?.podeCancelar : segundos > 0;
  }

  constructor(
    private readonly nfceService: NfceService,
    private readonly modalService: ModalService,
    destroyRef: DestroyRef
  ) {
    destroyRef.onDestroy(() => {
      this.$.forEach(($) => $.unsubscribe());
      if (this.timer) clearInterval(this.timer);
    });
  }

  ngOnInit(): void {
    this.startCountdown();
    this.buildMenu();
  }

  private startCountdown() {
    const update = () =>
      this.segundosRestantes.set(
        segundosParaCancelar(this.nfce?.limiteCancelamento)
      );

    update();

    if (this.autorizada && this.segundosRestantes() !== null) {
      this.timer = setInterval(() => {
        update();
        if ((this.segundosRestantes() ?? 0) <= 0 && this.timer) {
          clearInterval(this.timer);
          this.timer = undefined;
          this.buildMenu();
        }
      }, 1000);
    }
  }

  private buildMenu() {
    this.items.set([]);

    if (this.canDownloadFiles || this.rejeitada) {
      this.addOption("Baixar XML", "pi-download", () => this.download("xml"));
    }

    if (this.canDownloadFiles) {
      this.addOption("Baixar DANFCE (PDF)", "pi-file-pdf", () =>
        this.download("pdf")
      );
      this.addOption("Enviar por e-mail", "pi-envelope", () =>
        this.promptEmail()
      );
    }

    if (this.cancelada) {
      this.addOption("Baixar XML de cancelamento", "pi-file", () =>
        this.download("cancelamento-xml")
      );
    }

    if (this.podeCancelar) {
      this.addOption("Solicitar cancelamento", "pi-times", () =>
        this.promptCancel()
      );
    }

    if (this.autorizada || this.cancelada) {
      this.addOption("Consultar cancelamento", "pi-history", () =>
        this.showCancellationStatus()
      );
    }

    if (this.rejeitada) {
      this.addOption("Validar XML", "pi-external-link", () => {
        window.open(
          "https://validador.nfe.tecnospeed.com.br/",
          "_blank",
          "noopener,noreferrer"
        );
      });
    }
  }

  private promptCancel() {
    const restante = this.tempoRestante();

    this.modalService.deletion(
      "Solicitar cancelamento",
      `A NFC-e só pode ser cancelada dentro do prazo da SEFAZ (30 minutos após a autorização, por padrão)${
        restante ? ` — restam ${restante}` : ""
      }. Informe o motivo.`,
      (value) => {
        const justificativa = (value ?? "").trim();
        if (justificativa.length < 15) {
          this.modalService.error(
            "A justificativa deve conter no mínimo 15 caracteres."
          );
          return;
        }
        this.cancel(justificativa);
      },
      undefined,
      {
        fieldName: "justificativa",
        label: "Justificativa",
        required: true,
        type: "textarea",
        hint: "Mínimo de 15 caracteres.",
        invalidMessage: "A justificativa deve conter no mínimo 15 caracteres.",
        isValid: (value) => (value?.trim().length ?? 0) >= 15
      }
    );
  }

  private cancel(justificativa: string) {
    this.$.push(
      this.nfceService
        .requestNfceCancellation(this.nfce?.id ?? "", justificativa)
        .subscribe({
          next: () => {
            this.modalService.success(
              "Solicitação de cancelamento enviada. O cancelamento é confirmado pela SEFAZ em instantes; use “Consultar cancelamento” ou sincronize a lista.",
              () => this.changed.emit()
            );
          },
          error: (err: HttpErrorResponse) => {
            this.modalService.error(
              this.extractErrorMessage(
                err,
                "Ocorreu um erro ao solicitar o cancelamento da NFC-e."
              )
            );
          }
        })
    );
  }

  private showCancellationStatus() {
    this.$.push(
      this.nfceService.fetchCancellationStatus(this.nfce?.id ?? "").subscribe({
        next: (res) => {
          const data = res?.data;
          const linhas = [
            data?.status ? `Situação: ${data.status}` : "",
            data?.cStat ? `cStat: ${data.cStat}` : "",
            data?.protocolo ? `Protocolo: ${data.protocolo}` : "",
            data?.date ? `Data: ${data.date}` : "",
            data?.respostaSefaz ? `SEFAZ: ${data.respostaSefaz}` : "",
            data?.justificativa ? `Justificativa: ${data.justificativa}` : ""
          ].filter(Boolean);

          this.modalService.info(
            linhas.length
              ? linhas.join("\n")
              : res?.message ||
                  "Nenhuma solicitação de cancelamento encontrada.",
            "Cancelamento da NFC-e",
            () => this.changed.emit()
          );
        },
        error: (err: HttpErrorResponse) => {
          this.modalService.error(
            this.extractErrorMessage(
              err,
              "Não foi possível consultar o cancelamento."
            )
          );
        }
      })
    );
  }

  private promptEmail() {
    this.modalService.input(
      "Enviar NFC-e por e-mail",
      "Informe o e-mail do consumidor. Deixe em branco para reenviar ao e-mail informado na nota.",
      (value) => {
        const email = (value ?? "").trim();
        this.sendEmail(email ? [email] : undefined);
      },
      undefined,
      {
        fieldName: "email",
        label: "E-mail",
        required: false,
        type: "text"
      }
    );
  }

  private sendEmail(destinatarios?: string[]) {
    this.$.push(
      this.nfceService.sendEmail(this.nfce?.id ?? "", destinatarios).subscribe({
        next: () => this.modalService.success("E-mail enviado com sucesso!"),
        error: (err: HttpErrorResponse) => {
          this.modalService.error(
            this.extractErrorMessage(err, "Não foi possível enviar o e-mail.")
          );
        }
      })
    );
  }

  download(type: NfceDownloadType) {
    const id = this.nfce?.id ?? "";

    if (shouldUseExternalDownloadLink()) {
      const externalTab = preOpenExternalDownloadTab();
      this.$.push(
        this.nfceService.getExternalNfceDownloadLink(id, type).subscribe({
          next: ({ url }) => {
            if (!openInExternalBrowser(url, externalTab)) {
              this.modalService.error(
                "Não foi possível abrir o download no navegador externo."
              );
            }
          },
          error: () => {
            externalTab?.close();
            this.modalService.error(
              "Não foi possível preparar o link externo de download."
            );
          }
        })
      );
      return;
    }

    const popup = openDownloadPopupIfNeeded();

    this.$.push(
      this.nfceService.downloadNfceFile(id, type).subscribe({
        next: (blob) => {
          const extension = type === "pdf" ? "pdf" : "xml";
          const suffix = type === "cancelamento-xml" ? "-cancelamento" : "";
          saveBlobFile(
            blob,
            this.sanitizeFilename(
              `NFCe-S${this.nfce?.serie}-N${this.nfce?.numero}${suffix}.${extension}`
            ),
            popup
          );
          this.modalService.success(
            "Download iniciado. Verifique os arquivos do seu dispositivo."
          );
        },
        error: () => {
          popup?.close();
          this.modalService.error("Não foi possível baixar o arquivo da nota.");
        }
      })
    );
  }

  showError() {
    if (!this.rejeitada) {
      return this.modalService.info(
        "Não é possível visualizar o erro de uma nota que não foi rejeitada.",
        "Ação não permitida"
      );
    }

    this.modalService.info(
      this.nfce?.mensagem ?? "Sem detalhes da rejeição.",
      "Motivo da rejeição"
    );
  }

  copyToClipboard(id: string | null | undefined) {
    if (!id) return;

    navigator.clipboard
      .writeText(`ID: ${id} `)
      .then(() =>
        this.modalService.success("Copiado para a área de transferência!")
      )
      .catch(() => this.modalService.error("Falha ao copiar!"));
  }

  showOverlay(event: Event) {
    this.overlay.toggle(event);
  }

  private addOption(label: string, icon: string, command: () => void) {
    this.items.update((old) => [
      ...old,
      { label, icon: `pi ${icon}`, command }
    ]);
  }

  private extractErrorMessage(err: HttpErrorResponse, fallback: string) {
    const message =
      err?.error?.error?.data?.fields?.["justificativa"] ||
      err?.error?.error?.message ||
      err?.error?.message ||
      err?.message ||
      fallback;

    return Array.isArray(message) ? message.join("\n") : String(message);
  }

  private sanitizeFilename(filename: string): string {
    return filename
      .normalize("NFD")
      .replaceAll(/[̀-ͯ]/g, "")
      .replaceAll(/[^a-zA-Z0-9._-]/g, "_")
      .replaceAll(/_+/g, "_")
      .trim();
  }
}
